import { Controller } from "@hotwired/stimulus"

const AUTOCOMPLETE_TYPES = {
  street: ['address'], city: ['(cities)'], county: ['(regions)'], zip: ['(regions)']
}

const COMPONENT_MAP = [
  { target: 'street',  types: ['street_number', 'route'], join: ' ' },
  { target: 'city',    types: ['locality', 'sublocality'] },
  { target: 'county',  types: ['administrative_area_level_2', 'locality'] },
  { target: 'zip',     types: ['postal_code'] },
  { target: 'state',   types: ['administrative_area_level_1'], select: true },
  { target: 'country', types: ['country'], select: true }
]

export default class extends Controller {
  static targets = ["street", "city", "county", "zip", "state", "country", "zipLabel"]
  static values = { key: String }

  connect() {
    this.updateZipLabel()

    if (!this.keyValue) return
    this.instances = []

    if (window.google?.maps?.places) return this.init()

    const existing = window._initGooglePlaces
    window._initGooglePlaces = () => { existing?.(); this.init() }

    if (window._googlePlacesLoading) return

    window._googlePlacesLoading = true
    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${this.keyValue}&libraries=places&callback=_initGooglePlaces`
    script.async = true
    document.head.appendChild(script)
  }

  init() {
    Object.entries(AUTOCOMPLETE_TYPES).forEach(([name, types]) => {
      const el = this.targetFor(name)
      if (!el) return

      const ac = new google.maps.places.Autocomplete(el, {
        types, fields: ['address_components'], componentRestrictions: this.restriction()
      })
      ac.addListener('place_changed', () => this.populate(ac.getPlace(), name))
      el.setAttribute('placeholder', '')
      this.instances.push(ac)
    })
  }

  stateChanged() {
    this.activateAndRevalidate()
  }

  countryChanged() {
    this.updateZipLabel()
    this.clearTextFields()
    if (this.hasStateTarget) {
      this.stateTarget.value = ''
      $(this.stateTarget).trigger('change')
    }
    this.instances.forEach((ac) => ac.setComponentRestrictions(this.restriction()))
  }

  clearTextFields() {
    ['street', 'city', 'county', 'zip'].forEach((name) => {
      const el = this.targetFor(name)
      if (el) $(el).val('').trigger('keyup')
    })
    this.activateAndRevalidate()
  }

  populate(place, source) {
    if (!place?.address_components) return

    COMPONENT_MAP.forEach((mapping) => {
      const el = this.targetFor(mapping.target)
      if (!el) return

      const values = mapping.types.map((t) =>
        place.address_components.find((c) => c.types.includes(t))?.long_name
      ).filter(Boolean)

      if (!values.length && source !== 'street') return

      let value = ''
      if (values.length) {
        value = mapping.join ? values.join(mapping.join) : values[0]
      }

      if (mapping.select) {
        const option = value && Array.from(el.options).find((o) => o.text.includes(value))
        el.value = option ? option.value : ''
      } else {
        $(el).val(value).trigger('keyup')
      }
    })

    if (this.hasCountryTarget && typeof handleAddressFormDisplaying === 'function') {
      handleAddressFormDisplaying(this.countryTarget.id)
    }
    this.activateAndRevalidate()
  }

  activateAndRevalidate() {
    mdFormHelper.activateMdFields($(this.element))
    materialValidator.validatableFields(this.element).forEach((f) => materialValidator.showError(f))
  }

  restriction() {
    return this.isUnitedStates() ? { country: 'us' } : {}
  }

  isUnitedStates() {
    if (!this.hasCountryTarget) return true
    const key = this.countryTarget.selectedOptions[0]?.getAttribute('translation_key')
    return !key || key === 'united_states_of_america'
  }

  updateZipLabel() {
    if (!this.hasZipLabelTarget) return
    const { postCode, postalCode } = this.zipLabelTarget.dataset
    this.zipLabelTarget.textContent = this.isUnitedStates() ? postCode : postalCode
  }

  targetFor(name) {
    const cap = name[0].toUpperCase() + name.slice(1)
    return this[`has${cap}Target`] ? this[`${name}Target`] : null
  }
}
